import { useEffect, useState, useMemo } from "react";
import { PATHS } from "../../config/permissionsPaths";
import useAuth from "../../hooks/useAuth";
import styled from "styled-components";
import { usePatientData } from "../../context/PatientDataContext";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import ActivityModal from "../Modals/ActivityModal";
import Accordion from "../Common/Accordion";
import {
  calcularTAM,
  calcularCKDEPI2021,
  calcularIMC,
  calcularEdad,
} from "../../utils/calculatorUtils";

const SidebarContainer = styled.div`
  width: ${(props) => (props.$collapsed ? "0" : "400px")};
  height: calc(100vh - (var(--navbar-height) + var(--footer-height)));
  background-color: ${(props) => props.theme.sidebarBackground};
  color: ${(props) => props.theme.sidebarText};
  transition: width 0.3s ease;
  box-shadow: ${(props) =>
    props.$collapsed ? "none" : "-2px 0 5px rgba(0,0,0,0.1)"};
  padding: ${(props) => (props.$collapsed ? "0" : "8px")};
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-sizing: border-box;
  overflow: hidden;
`;

const SidebarContent = styled.div`
  opacity: ${(props) => (props.$collapsed ? 0 : 1)};
  transition: opacity 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  overflow-y: auto;
  overflow-x: hidden;
`;

const PhysioSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 2px 0;
`;

const PhysioSubHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 10px;
  font-weight: 700;
  color: #1e40af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding-bottom: 3px;
  border-bottom: 1px solid #e2e8f0;
  margin-top: ${(props) => (props.$mt ? props.$mt : "0")};
  font-family: var(--font-body);
`;

const PhysioGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PhysioCard = styled.div`
  padding: 5px 8px;
  border: 1px solid #e2e8f0;
  border-radius: var(--radius-sm, 4px);
  background-color: #f8fafc;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
`;

const PhysioCardLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const PhysioCardLabel = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: #475569;
  font-family: var(--font-body);
`;

const PhysioCardMeta = styled.span`
  font-size: 9px;
  color: #94a3b8;
  font-family: var(--font-body);
`;

const PhysioCardValue = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: ${(props) => props.$color || "#1e293b"};
  font-family: var(--font-mono);
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PhysioBadge = styled.span`
  font-size: 9px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 10px;
  background-color: ${(props) => props.$bg || "#f1f5f9"};
  color: ${(props) => props.$color || "#475569"};
  line-height: 1.3;
  display: inline-flex;
  align-items: center;
`;

const PhysioFooterHint = styled.div`
  font-size: 9px;
  color: #94a3b8;
  text-align: center;
  padding: 4px 2px 0 2px;
  font-style: italic;
  font-family: var(--font-body);
`;

const DxSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 2px 0;
`;

const DxCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  transition: all 0.15s ease;

  &:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
  }
`;

const DxHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const DxTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const DxCode = styled.span`
  font-weight: 700;
  color: #1e40af;
  background: #dbeafe;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 10.5px;
  font-family: monospace;
`;

const DxSeparator = styled.span`
  color: #94a3b8;
  font-weight: normal;
`;

const DxName = styled.span`
  color: #1e293b;
  font-weight: 500;
`;

const DxMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 9.5px;
  color: #64748b;
  margin-top: 2px;
`;

const DxFooterHint = styled.div`
  font-size: 9px;
  color: #94a3b8;
  text-align: center;
  padding: 4px 2px 0 2px;
  font-style: italic;
  font-family: var(--font-body);
`;

const Table = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  column-gap: 10px;
  row-gap: 6px;
  align-items: flex-start;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const QuestionCell = styled.div`
  font-size: var(--app-font-size);
  font-family: var(--font-body);
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  user-select: none;
`;

const MasInfoContainer = styled.div`
  grid-column: 1 / -1;
  width: 100%;
  font-size: 0.75rem;
  font-family: var(--font-body);
  color: #475569;
`;

const SwitchContainer = styled.div`
  position: relative;
  width: 70px;
  height: 24px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
  font-size: 12px;
  cursor: pointer;
  background-color: ${({ value, theme }) =>
    value === "si" ? theme.toggleSi : value === "neutral" ? theme.toggleNeutro : theme.toggleNo};
  transition: background-color 0.3s ease;
`;

const HiddenInput = styled.input`
  position: absolute;
  opacity: 0;
  pointer-events: none;
`;

const Label = styled.span`
  z-index: 2;
  user-select: none;
`;

const NeutralLabel = styled(Label)`
  opacity: 0.6;
`;

const Thumb = styled.div`
  position: absolute;
  top: 3px;
  width: 18px;
  height: 18px;
  background: white;
  border-radius: 50%;
  transition: left 0.3s ease;
  left: ${({ value }) =>
    value === "si"
      ? "calc(100% - 24px)"
      : value === "neutral"
      ? "calc(50% - 7px)"
      : "8px"};
`;

const ThreeStateSwitch = ({ name, value, onChange, disabledMiddle }) => {
  const triggerChange = (newValue) => {
    if (newValue === "neutral" && disabledMiddle) return;

    onChange({
      target: {
        name,
        value: newValue,
      },
    });
  };

  return (
    <SwitchContainer value={value}>
      <HiddenInput
        type="radio"
        name={name}
        value="no"
        checked={value === "no"}
        onChange={onChange}
      />

      <HiddenInput
        type="radio"
        name={name}
        value="neutral"
        checked={value === "neutral"}
        onChange={onChange}
        disabled={disabledMiddle}
      />

      <HiddenInput
        type="radio"
        name={name}
        value="si"
        checked={value === "si"}
        onChange={onChange}
      />

      <Label onClick={() => triggerChange("no")}>No</Label>
      <NeutralLabel onClick={() => triggerChange("neutral")}>-</NeutralLabel>
      <Label onClick={() => triggerChange("si")}>Sí</Label>

      <Thumb value={value} />
    </SwitchContainer>
  );
};

const Sidebar = ({ collapsed }) => {
  const {
    preguntasData,
    patientData,
    yearOldData,
    resultData,
    handleCloseModal,
    modalOpen,
    selectedActivity,
    enviarRespuestaPregunta,
  } = usePatientData();

  const [respuestas, setRespuestas] = useState({});
  const [acordeonActivo, setAcordeonActivo] = useState([]);
  const [todosAbiertos, setTodosAbiertos] = useState(false);
  const { hasPermission } = useAuth();

  useEffect(() => {
    if (preguntasData?.length) {
      setTodosAbiertos(acordeonActivo.length === preguntasData.length);
    }
  }, [acordeonActivo, preguntasData]);

  useEffect(() => {
    if (preguntasData?.length) {
      const iniciales = {};
      preguntasData.forEach((pregunta, idx) => {
        if (pregunta.resultado === "1") iniciales[`pregunta-${idx}`] = "si";
        else if (pregunta.resultado === "0")
          iniciales[`pregunta-${idx}`] = "no";
        else iniciales[`pregunta-${idx}`] = "neutral";
      });
      setRespuestas(iniciales);
    }
  }, [preguntasData]);

  const handleChange = (idx, preguntaId, value) => {
    const key = `pregunta-${idx}`;
    setRespuestas((prev) => ({
      ...prev,
      [key]: value,
    }));

    if (value === "si" || value === "no") {
      const resultado = value === "si" ? "1" : "0";
      enviarRespuestaPregunta(preguntaId, resultado);
    }
  };

  const toggleAcordeon = (index) => {
    setAcordeonActivo((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const toggleTodosAcordeones = () => {
    if (todosAbiertos) {
      setAcordeonActivo([]);
    } else {
      setAcordeonActivo(preguntasData.map((_, index) => index));
    }
  };

  const physioData = useMemo(() => {
    if (!resultData || !Array.isArray(resultData)) {
      return {
        tas: null,
        tad: null,
        creat: null,
        peso: null,
        talla: null,
        tam: null,
        tfg: null,
        imc: null,
      };
    }

    const matchCons = (item, id, regex) => {
      if (!item) return false;
      const c = Number(item.cons ?? item.consecutivo ?? item.Consecutivo);
      if (c === id) return true;
      if (regex && typeof item.APIM === "string") return regex.test(item.APIM);
      return false;
    };

    const extractNumber = (val) => {
      if (val === null || val === undefined) return null;
      if (typeof val === "number") return isNaN(val) ? null : val;
      const cleaned = String(val).replace(",", ".").replace(/[^\d.]/g, " ").trim();
      const num = parseFloat(cleaned);
      return isNaN(num) ? null : num;
    };

    const tasItem = resultData.find((r) => matchCons(r, 271, /sist[oó]lica/i));
    const tadItem = resultData.find((r) => matchCons(r, 272, /diast[oó]lica/i));
    const creatItem = resultData.find((r) => matchCons(r, 57, /creatinina/i));
    const pesoItem = resultData.find((r) => matchCons(r, 243, /peso/i));
    const tallaItem = resultData.find((r) => matchCons(r, 244, /talla/i));

    const tas = extractNumber(tasItem?.Cod_Diagnostico);
    const tad = extractNumber(tadItem?.Cod_Diagnostico);
    let creat = extractNumber(creatItem?.Cod_Diagnostico);
    if (creat && creat > 25) {
      creat = Math.round((creat / 88.4) * 100) / 100;
    }

    const peso = extractNumber(pesoItem?.Cod_Diagnostico);
    const talla = extractNumber(tallaItem?.Cod_Diagnostico);

    const rawAge =
      yearOldData?.anios !== undefined && yearOldData?.anios !== null
        ? yearOldData.anios
        : patientData?.Fecha_Nto
        ? calcularEdad(patientData.Fecha_Nto)
        : null;
    const age =
      rawAge !== null && rawAge !== "" && !isNaN(Number(rawAge))
        ? Number(rawAge)
        : null;
    const sex = patientData?.Sexo ?? null;

    const tam = tas && tad ? calcularTAM(tas, tad) : null;
    const tfg = creat && age && sex ? calcularCKDEPI2021(creat, age, sex) : null;
    const imc =
      peso && talla && age !== null && age >= 20
        ? calcularIMC(peso, talla, age)
        : null;

    return {
      tas,
      tad,
      creat,
      peso,
      talla,
      tam,
      tfg,
      imc,
      fechaTA: tasItem?.MaxDeFecha_APIM || tadItem?.MaxDeFecha_APIM || null,
      fechaCreat: creatItem?.MaxDeFecha_APIM || null,
      fechaAntropo: pesoItem?.MaxDeFecha_APIM || tallaItem?.MaxDeFecha_APIM || null,
    };
  }, [resultData, patientData, yearOldData]);

  const dxList = useMemo(() => {
    if (Array.isArray(patientData?.diagnosticos)) {
      return patientData.diagnosticos;
    }
    return [];
  }, [patientData]);

  const expandIconStyle = {
    cursor: "pointer",
    padding: "2px 4px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#2563eb",
  };

  return (
    <>
      {hasPermission(PATHS.PREGUNTAS) && (
        <SidebarContainer $collapsed={collapsed}>
          <SidebarContent $collapsed={collapsed}>
            <Accordion
              title="Apoyo para toma de decisiones"
              count={preguntasData?.length || 0}
              defaultOpen={true}
              fillSpace={true}
              fontSize="11px"
              headerPadding="6px 10px"
              rightAction={
                <span
                  onClick={toggleTodosAcordeones}
                  title={todosAbiertos ? "Cerrar todos" : "Abrir todos"}
                  style={expandIconStyle}
                >
                  {todosAbiertos ? (
                    <FaChevronUp size={12} />
                  ) : (
                    <FaChevronDown size={12} />
                  )}
                </span>
              }
            >
              {preguntasData?.map((pregunta, index) => {
                const key = `pregunta-${index}`;
                const valor = respuestas[key] || "neutral";
                const disableNeutral =
                  pregunta.resultado === "0" || pregunta.resultado === "1";
                const isExpanded = acordeonActivo.includes(index);

                return (
                  <Table key={index}>
                    <QuestionCell onClick={() => toggleAcordeon(index)}>
                      <span>{pregunta.pregunta}</span>
                      {pregunta.masinfo && (
                        <span
                          style={{
                            fontSize: "9px",
                            color: "#94a3b8",
                            marginTop: "3px",
                            flexShrink: 0,
                          }}
                        >
                          {isExpanded ? "▲" : "▼"}
                        </span>
                      )}
                    </QuestionCell>

                    <ThreeStateSwitch
                      name={key}
                      value={valor}
                      onChange={(e) =>
                        handleChange(index, pregunta.id, e.target.value)
                      }
                      disabledMiddle={disableNeutral}
                    />

                    {isExpanded && pregunta.masinfo && (
                      <MasInfoContainer>
                        {Array.isArray(pregunta.masinfo) ? (
                          <ul
                            style={{
                              margin: "4px 0 0 0",
                              paddingLeft: "18px",
                              lineHeight: "1.45",
                            }}
                          >
                            {pregunta.masinfo.map((info, i) => (
                              <li key={i} style={{ marginBottom: "3px" }}>
                                {info}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div style={{ marginTop: "4px", lineHeight: "1.45" }}>
                            {pregunta.masinfo}
                          </div>
                        )}
                      </MasInfoContainer>
                    )}
                  </Table>
                );
              })}
            </Accordion>

            <Accordion
              title="Valores calculados"
              count={
                [physioData.tam, physioData.tfg, physioData.imc].filter(Boolean)
                  .length
              }
              defaultOpen={true}
              fillSpace={true}
              fontSize="11px"
              headerPadding="6px 10px"
            >
              <PhysioSection>
                {!physioData.tam && !physioData.tfg && !physioData.imc ? (
                  <div
                    style={{
                      padding: "8px 10px",
                      fontSize: "11px",
                      color: "#94a3b8",
                      fontFamily: "var(--font-body)",
                    }}
                  >
                    Sin valores calculados
                  </div>
                ) : (
                  <PhysioGrid>
                    {/* TAM - solo si tiene valor */}
                    {physioData.tam && (
                      <PhysioCard>
                        <PhysioCardLeft>
                          <PhysioCardLabel>TAM (Tensión Arterial Media)</PhysioCardLabel>
                        </PhysioCardLeft>
                        <PhysioCardValue>
                          <span>{physioData.tam.valor} mmHg</span>
                          <PhysioBadge
                            $bg={physioData.tam.badgeBg}
                            $color={physioData.tam.color}
                          >
                            {physioData.tam.estado}
                          </PhysioBadge>
                        </PhysioCardValue>
                      </PhysioCard>
                    )}

                    {/* TFG - solo si tiene valor */}
                    {physioData.tfg && (
                      <PhysioCard>
                        <PhysioCardLeft>
                          <PhysioCardLabel>TFG (CKD-EPI 2021)</PhysioCardLabel>
                        </PhysioCardLeft>
                        <PhysioCardValue>
                          <span>{physioData.tfg.valor} mL/min</span>
                          <PhysioBadge
                            $bg={physioData.tfg.badgeBg}
                            $color={physioData.tfg.color}
                          >
                            {physioData.tfg.estadio} • {physioData.tfg.descripcion}
                          </PhysioBadge>
                        </PhysioCardValue>
                      </PhysioCard>
                    )}

                    {/* IMC - solo si tiene valor */}
                    {physioData.imc && (
                      <PhysioCard>
                        <PhysioCardLeft>
                          <PhysioCardLabel>IMC (Índice Masa Corporal)</PhysioCardLabel>
                        </PhysioCardLeft>
                        <PhysioCardValue>
                          <span>{physioData.imc.valor} kg/m²</span>
                          <PhysioBadge
                            $bg={physioData.imc.badgeBg}
                            $color={physioData.imc.color}
                          >
                            {physioData.imc.estado}
                          </PhysioBadge>
                        </PhysioCardValue>
                      </PhysioCard>
                    )}
                  </PhysioGrid>
                )}
              </PhysioSection>
            </Accordion>

            <Accordion
              title="Histórico de diagnósticos"
              count={dxList.length}
              defaultOpen={true}
              fillSpace={true}
              fontSize="11px"
              headerPadding="6px 10px"
            >
              <DxSection>
                {dxList.length === 0 ? (
                  <div
                    style={{
                      padding: "8px 10px",
                      fontSize: "11px",
                      color: "#94a3b8",
                      fontFamily: "var(--font-body)",
                    }}
                  >
                    Sin diagnósticos registrados
                  </div>
                ) : (
                  dxList.map((dx, idx) => {
                    let descrip = dx.Descrip || dx.nombre || "";
                    const consec = String(dx.consecutivo || "");
                    if (consec === "580" && !descrip.startsWith("C Ext - ")) {
                      descrip = `C Ext - ${descrip}`;
                    } else if (consec === "581" && !descrip.startsWith("Urg - ")) {
                      descrip = `Urg - ${descrip}`;
                    }

                    const textoDx = dx.fecha ? `${dx.fecha} - ${descrip}` : descrip;

                    return (
                      <DxCard key={idx}>
                        <DxHeader>
                          <DxTitle>
                            <DxName>{textoDx}</DxName>
                          </DxTitle>
                        </DxHeader>
                      </DxCard>
                    );
                  })
                )}
              </DxSection>
            </Accordion>
          </SidebarContent>

          <ActivityModal
            isOpen={modalOpen}
            onClose={handleCloseModal}
            activity={selectedActivity}
          />
        </SidebarContainer>
      )}
    </>
  );
};

export default Sidebar;
